import { useEffect, useState } from 'react';
import { SingleValue } from 'react-select';
import Dropdown from '../form-inputs/inputDropdown';
import Button from '../buttons/baseButton';
import PostUpdateCampaignBilling from '../../services/adapters/updateCampaignBilling';
import type { OptionType } from '../../types/forms.type';
import { Channel } from '../../types/enums';
import GetOrganisations from '../../services/adapters/organisations';

interface UpdateCampaignBillingProps {
  onBillingUpdated: () => void;
}

export default function UpdateCampaignBilling({ onBillingUpdated }: UpdateCampaignBillingProps) {
  const [organisationDropdownOptions, setOrganisationDropdownOptions] = useState<OptionType[]>([]);
  const [organisationDropdownState, setOrganisationDropdownState] = useState('');
  const [isCampaignEnabledDropdownState, setIsCampaignEnabledDropdownState] = useState('');
  const [channelDropdownState, setChannelDropdownState] = useState('');
  const [changeAmount, setChangeAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrganisations = async () => {
      try {
        const adapter = new GetOrganisations();
        const response = await adapter.adaptResponse();
        const options = response.organisationNames.map((name) => ({ label: name, value: name }));
        setOrganisationDropdownOptions(options);
      } catch (err) {
        setErrorMessage(err instanceof Error ? err.message : 'Failed to fetch organisations');
      }
    };

    fetchOrganisations();
  }, []);

  const channelDropdownOptions: OptionType[] = [
    { label: '', value: '' },
    { label: Channel.Letter, value: Channel.BackendLetter },
    { label: Channel.SMS, value: Channel.BackendSMS },
  ];
  const isCampaignEnabledoptions: OptionType[] = [
    { label: '', value: '' },
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
  ];

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const handleOrganisationChange = (_: string, value: SingleValue<OptionType>) => {
    const selectedValue = value ? value.value : '';
    setOrganisationDropdownState(selectedValue);
  };

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const handleChannelChange = (_: string, value: SingleValue<OptionType>) => {
    const selectedValue = value ? value.value : '';
    setChannelDropdownState(selectedValue);
  };

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const handleIsCampaignEnabledChange = (_: string, value: SingleValue<OptionType>) => {
    const selectedValue = value ? value.value : '';
    setIsCampaignEnabledDropdownState(selectedValue);
  };

  const clearFormFields = () => {
    setOrganisationDropdownState('');
    setIsCampaignEnabledDropdownState('');
    setChannelDropdownState('');
    setChangeAmount('');
  };

  const handleReset = () => {
    clearFormFields();
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const handleSubmit = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!organisationDropdownState) {
      setErrorMessage('Organisation is required');
      return;
    }

    const hasChannelAndAmount = channelDropdownState && changeAmount;
    const hasCampaignEnabled = isCampaignEnabledDropdownState;

    if (!hasChannelAndAmount && !hasCampaignEnabled) {
      setErrorMessage('Please set either (Channel and Change Amount) or Campaign Enabled status');
      return;
    }

    if (channelDropdownState && !changeAmount) {
      setErrorMessage('Change amount is required when channel is selected');
      return;
    }
    if (changeAmount && !channelDropdownState) {
      setErrorMessage('Channel is required when change amount is entered');
      return;
    }
    let changeAmountNumber: number | null = null;
    if (changeAmount) {
      changeAmountNumber = parseInt(changeAmount, 10);
      if (Number.isNaN(changeAmountNumber)) {
        setErrorMessage('Change amount must be a valid integer');
        return;
      }
    }

    try {
      setLoading(true);
      const updateInstance = new PostUpdateCampaignBilling();
      const result = await updateInstance.adaptResponse({
        organisationDisplayName: organisationDropdownState,
        isCampaignEnabled: isCampaignEnabledDropdownState
          ? isCampaignEnabledDropdownState === 'true'
          : null,
        channelName: channelDropdownState || null,
        changeAmount: changeAmountNumber,
      });

      setSuccessMessage(`Successfully updated ${result.organisationDisplayName}`);

      clearFormFields();
      onBillingUpdated();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to update campaign billing');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-pipgrey-light rounded-md p-2">
      <h3 className="m-2 font-nunito">Update Allowance</h3>
      <div className="flex flex-row gap-4 items-end m-2">
        <div className="flex-1">
          <Dropdown
            id="organisations"
            label="Organisation"
            placement="top"
            onChange={handleOrganisationChange}
            selected={organisationDropdownState}
            options={organisationDropdownOptions}
          />
        </div>

        <div className="flex-[0.5]">
          <Dropdown
            id="is-campaign-enabled"
            label="Campaign Enabled?"
            placement="top"
            onChange={handleIsCampaignEnabledChange}
            selected={isCampaignEnabledDropdownState}
            options={isCampaignEnabledoptions}
          />
        </div>

        <div className="flex-[0.5]">
          <Dropdown
            id="channel"
            label="Channel"
            placement="top"
            onChange={handleChannelChange}
            selected={channelDropdownState}
            options={channelDropdownOptions}
          />
        </div>

        <div className="flex-[0.5]">
          <label htmlFor="allowance-change-amount" className="text-secondary text-sm block mb-1">
            Allowance Change Amount
            <input
              id="allowance-change-amount"
              type="number"
              className="input-default w-full mt-1 p-2"
              value={changeAmount}
              onChange={(e) => setChangeAmount(e.target.value)}
              placeholder="Enter amount to change allowance by"
            />
          </label>
        </div>

        <div className="flex flex-col gap-2">
          <Button
            id="reset-billing-button"
            className="button-default"
            label="Reset"
            onClick={handleReset}
          />
          <Button
            id="update-billing-button"
            className="button-green w-40"
            label={loading ? 'Updating...' : 'Update Allowance'}
            onClick={handleSubmit}
            isDisabled={loading}
          />
        </div>
      </div>

      {errorMessage && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
          {errorMessage}
        </div>
      )}

      {successMessage && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded text-green-600 text-sm">
          {successMessage}
        </div>
      )}
    </div>
  );
}
